import React, { useMemo } from 'react';
import { BackgroundThemeRevision, ColorId } from 'referral-types';
import chroma from 'chroma-js';
import { useTheme } from '../../../context/theme/useTheme';
import { useBodyStyleVersion } from '../../../hooks/useBodyStyleVersion';
import { getCssVariableForColorId } from '../../../constants';
import { rgbStringToHex } from '../../../utils/colorUtils';

/*

Apartment Floor - Brick-like rectangular pattern with color variations
Takes 1 color input: user-selectable c1

Original color: #2A6066

 */

const ApartmentFloorBackground: React.FC<{ themeRevision?: Partial<BackgroundThemeRevision> }> = (props) => {
  const { colorId1 } = props.themeRevision || {};
  const { isDarkMode } = useTheme();
  const bodyStyleVersion = useBodyStyleVersion();

  const computedColors = useMemo(() => {
    const colorStyles1 = getCssVariableForColorId(colorId1 || ColorId.Gray4, isDarkMode);

    const rootStyles = getComputedStyle(document.body);
    const c1 = rgbStringToHex(rootStyles.getPropertyValue(colorStyles1).trim() || '42 96 102');

    // Generate color variations from the base color
    const baseColor = chroma(c1);
    const variations = [
      baseColor.hex(),
      baseColor.darken(1.5).hex(),
      baseColor.darken(0.5).hex(),
      baseColor.brighten(1.5).hex(),
      baseColor.brighten(0.8).hex(),
      baseColor.darken(2).hex(),
      baseColor.darken(1.8).hex(),
      baseColor.brighten(0.3).hex(),
      baseColor.darken(2.5).hex(),
    ];

    return variations;
  }, [colorId1, isDarkMode, bodyStyleVersion]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        backgroundColor: computedColors[0],
        pointerEvents: 'none',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox="0 0 2000 1000"
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
        }}
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <rect id="apartment-a" width="201" height="51" />
          <rect id="apartment-b" width="251" height="51" />
          <rect id="apartment-c" width="351" height="51" />
          <rect id="apartment-d" width="401" height="51" />
          <rect id="apartment-e" width="501" height="51" />
          <pattern id="apartment-p" width="2000" height="1000" patternUnits="userSpaceOnUse">
            <g id="apartment-B">
              <g id="apartment-A">
                <use fill={computedColors[0]} href="#apartment-a" />
                <use fill={computedColors[1]} href="#apartment-c" x="200" />
                <use fill={computedColors[2]} href="#apartment-d" x="550" />
                <use fill={computedColors[3]} href="#apartment-b" x="950" />
                <use fill={computedColors[4]} href="#apartment-a" x="1200" />
                <use fill={computedColors[5]} href="#apartment-e" x="1400" />
                <use fill={computedColors[0]} href="#apartment-b" x="1900" />
                <use fill={computedColors[6]} href="#apartment-a" x="2150" />
                <use fill={computedColors[0]} href="#apartment-e" x="2350" />
                <use fill={computedColors[7]} href="#apartment-d" x="2850" />
                <use fill={computedColors[2]} href="#apartment-a" x="3250" />
                <use fill={computedColors[5]} href="#apartment-b" x="3450" />
                <use fill={computedColors[1]} href="#apartment-c" x="3700" />
                <use fill={computedColors[4]} href="#apartment-c" x="4050" />
                <use fill={computedColors[8]} href="#apartment-a" x="4400" />
                <use fill={computedColors[3]} href="#apartment-e" x="4600" />
                <use fill={computedColors[6]} href="#apartment-a" x="4900" />
                <use fill={computedColors[7]} href="#apartment-b" x="5100" />
                <use fill={computedColors[3]} href="#apartment-c" x="5350" />
                <use fill={computedColors[8]} href="#apartment-d" x="5700" />
                <use fill={computedColors[4]} href="#apartment-e" x="6100" />
                <use fill={computedColors[2]} href="#apartment-b" x="6600" />
                <use fill={computedColors[6]} href="#apartment-a" x="6850" />
                <use fill={computedColors[1]} href="#apartment-b" x="7050" />
                <use fill={computedColors[0]} href="#apartment-c" x="7300" />
                <use fill={computedColors[7]} href="#apartment-c" x="7650" />
                <use fill={computedColors[5]} href="#apartment-e" x="8000" />
              </g>
              <use href="#apartment-A" x="-1500" y="50" />
              <use href="#apartment-A" x="-3050" y="100" />
              <use href="#apartment-A" x="-150" y="150" />
              <use href="#apartment-A" x="-4350" y="200" />
              <use href="#apartment-A" x="-1900" y="250" />
              <use fill={computedColors[1]} href="#apartment-b" y="250" />
              <use href="#apartment-A" x="-2900" y="300" />
              <use href="#apartment-A" x="-3800" y="350" />
              <use fill={computedColors[8]} href="#apartment-b" y="350" />
              <use href="#apartment-A" x="-2350" y="400" />
              <use href="#apartment-A" x="-900" y="450" />
              <use fill={computedColors[7]} href="#apartment-c" x="-50" y="450" />
            </g>
            <use href="#apartment-B" x="-2000" y="500" />
            <rect fill={computedColors[3]} width="150" height="50" y="500" />
            <rect fill={computedColors[3]} width="200" height="50" y="550" />
            <rect fill={computedColors[4]} width="200" height="50" y="650" />
            <rect fill={computedColors[5]} width="250" height="50" y="700" />
            <rect fill={computedColors[8]} width="150" height="50" y="750" />
            <rect fill={computedColors[7]} width="100" height="50" y="850" />
            <rect fill={computedColors[4]} width="250" height="50" y="900" />
            <rect fill={computedColors[3]} width="50" height="50" y="950" />
          </pattern>
        </defs>
        <rect fill="url(#apartment-p)" width="100%" height="100%" />
        <rect fill={computedColors[0]} fillOpacity=".5" width="100%" height="100%" />
      </svg>
    </div>
  );
};

export default ApartmentFloorBackground;
